import { supabase, TABLES } from './db';

// On-Point vs Off-Topic — one vote per user per task. The marketplace feed
// signal that replaces the old "Used" boolean. A vote can be switched or
// cleared (toggle the same choice off).

export type RatingVote = 'on_point' | 'off_topic';

export interface RatingSummary {
  onPoint: number;
  offTopic: number;
}

/** Cast or switch this user's vote on a task. */
export async function castVote(taskId: string, userId: string, vote: RatingVote): Promise<void> {
  const { error } = await supabase
    .from(TABLES.TASK_RATINGS)
    .upsert(
      { task_id: taskId, user_id: userId, vote, updated_at: new Date().toISOString() },
      { onConflict: 'task_id,user_id' }
    );
  if (error) throw error;
}

/** Remove this user's vote (toggle off). */
export async function clearVote(taskId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLES.TASK_RATINGS)
    .delete()
    .eq('task_id', taskId)
    .eq('user_id', userId);
  if (error) throw error;
}

/** Aggregate on-point / off-topic tallies for a set of tasks. */
export async function getRatingSummaries(taskIds: string[]): Promise<Record<string, RatingSummary>> {
  const out: Record<string, RatingSummary> = {};
  if (taskIds.length === 0) return out;
  const { data, error } = await supabase
    .from(TABLES.TASK_RATINGS)
    .select('task_id, vote')
    .in('task_id', taskIds);
  if (error || !data) return out;
  for (const row of data as Array<{ task_id: string; vote: RatingVote }>) {
    const s = (out[row.task_id] ??= { onPoint: 0, offTopic: 0 });
    if (row.vote === 'on_point') s.onPoint += 1;
    else s.offTopic += 1;
  }
  return out;
}

/** This user's current vote on each of the given tasks (absent = no vote). */
export async function getMyVotes(taskIds: string[], userId: string): Promise<Record<string, RatingVote>> {
  const out: Record<string, RatingVote> = {};
  if (taskIds.length === 0) return out;
  const { data, error } = await supabase
    .from(TABLES.TASK_RATINGS)
    .select('task_id, vote')
    .eq('user_id', userId)
    .in('task_id', taskIds);
  if (error || !data) return out;
  for (const row of data as Array<{ task_id: string; vote: RatingVote }>) {
    out[row.task_id] = row.vote;
  }
  return out;
}

/** On-point score as a percentage of total votes (0 when no votes). */
export function onPointScore(summary: RatingSummary | undefined): number {
  if (!summary) return 0;
  const total = summary.onPoint + summary.offTopic;
  return total === 0 ? 0 : Math.round((summary.onPoint / total) * 100);
}
